<template>
  <section class="about-hero">
    <div class="about-hero-content">
      <h2 class="about-title">{{ t('homePage.about.title') }}</h2>
      <div class="about-main">
        <div class="about-info">
          <h3>{{ t('homePage.about.companyName') }}</h3>
          <div class="about-divider"></div>
          <div class="about-desc">
            <p>{{ t('homePage.about.descriptions') }}</p>
          </div>
          <router-link to="/about" exact-active-class="active">
            <button class="about-btn">{{ t('homePage.about.moreButton') }}</button>
          </router-link>
        </div>
        <div class="about-stats" ref="statsRef">
          <div v-for="(stat, index) in animatedStats" :key="index" class="stat">
            <div class="num">
              <span>{{ stat.displayNumber }}</span>

              <template v-if="index === 3">
                <span class="suffix-small">{{ (stat.suffix.split('+')[0] || '') }}</span>
                <span v-if="stat.suffix.includes('+')" class="sup">+</span>
                <span class="suffix-small">{{ (stat.suffix.split('+')[1] || '') }}</span>
              </template>
              <template v-else>
                <span v-if="[1, 2].includes(index) && stat.suffix.includes('+')">
                  <span class="sup">+</span>
                </span>
                <span :class="{ 'suffix-small': [0, 1, 2, 3, 4, 5].includes(index) }">{{ stat.suffix.replace('+', '')
                }}</span>
              </template>
            </div>
            <div class="label">{{ stat.label }}</div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { useI18n } from 'vue-i18n'
import { computed, ref, onMounted, onUnmounted, watch } from 'vue'

export default {
  name: 'AboutUs',
  setup() {
    const { t, tm } = useI18n()
    const stats = computed(() => {
      const statsData = tm('homePage.about.stats')
      return Array.isArray(statsData) ? statsData : []
    })

    const animatedStats = ref(stats.value.map(item => ({ ...item, displayNumber: 0, suffix: '' })))
    const statsRef = ref(null)
    let observer = null

    const startCounting = () => {
      animatedStats.value = stats.value.map(item => ({ ...item, displayNumber: 0, suffix: '' }))

      animatedStats.value.forEach(item => {
        // 去掉逗号再提取数字（适配英文 2,000,000+）
        const numPart = (item.number || '').replace(/,/g, '');
        const match = numPart.match(/\d+/);
        const targetNumber = match ? parseInt(match[0], 10) : 0;

        // 从原始 number 中去掉“起始的数字+逗号”，保留后缀（如 '万+台' / '+ units'）
        const suffix = (item.number || '').replace(/^\s*\d[\d,]*/, '');
        item.suffix = suffix;

        let current = 0;
        const duration = 2000;
        const interval = 50;
        const steps = Math.ceil(duration / interval);
        const increment = targetNumber / steps;

        const timer = setInterval(() => {
          current += increment;
          if (current >= targetNumber) {
            current = targetNumber;
            clearInterval(timer);
          }
          item.displayNumber = Math.floor(current);
        }, interval);
      });
    };

    // 监听 stats 变化，语言切换时重新触发动画
    watch(stats, () => {
      startCounting()
    })

    onMounted(() => {
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            startCounting()
            observer.unobserve(entry.target)
          }
        })
      })

      if (statsRef.value) {
        observer.observe(statsRef.value)
      }
    })

    onUnmounted(() => {
      if (observer) {
        observer.disconnect()
      }
    })

    return {
      t,
      animatedStats,
      statsRef
    }
  }
}
</script>

<style scoped>
.about-hero {
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: url('../../assets/homePage/aboutUs/con01.jpg');
  background-repeat: no-repeat;
  background-size: cover;
  padding-bottom: 100px;
  margin-bottom: 200px;
}

.about-hero-content {
  position: relative;
  z-index: 1;
  width: 90%;
  /* 需要适配 */
  margin: 0 auto;
  text-align: center;
  color: #fff;
}

.about-title {
  font-weight: bold;
  margin-bottom: 120px;
  letter-spacing: 2px;
  text-align: center;
  position: relative;
  margin-top: 80px;
  font-size: 2.2rem;
}

.about-title::after {
  content: '';
  display: block;
  width: 80px;
  height: 4px;
  background: #fff;
  margin: 10px auto 0 auto;
}

.about-main {
  display: flex;
  gap: 2vw;
  margin-top: 100px;
  background: none;
  justify-content: center;
}

.about-info {
  width: 50vw;
  background: rgba(5, 5, 5, 0.45);
  padding: 3vw 3vw 3vw 3vw;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-bottom: 100px;
}

.about-info h3 {
  font-size: 2rem;
  /* 需要适配 */
  font-weight: bold;
  margin-bottom: 1vw;
}

.about-desc {
  font-size: 1.2rem;
  /* 需要适配 */
  color: #fff;
  opacity: 0.95;
  margin-bottom: 30px;
  text-align: left;
  line-height: 2;
  /* 需要适配 */
}

.about-divider {
  width: 80px;
  height: 3px;
  background: #fff;
  border-radius: 2px;
}

.about-btn {
  background: #f98335;
  color: #fff;
  border: none;
  padding: 10px 50px;
  font-size: 1.1rem;
  /* 需要适配 */
  cursor: pointer;
  margin-top: 1vw;
  transition: background 0.2s;
}

.about-btn:hover {
  background: #ff6600;
}

.about-stats {
  width: 50vw;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 10px;
}

.stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(5, 5, 5, 0.45);
}

.num {
  font-size: 3rem;
  /* 需要适配 */
  font-weight: bold;
  color: #f98335;
  margin-bottom: 0.5vw;
  white-space: nowrap;
}

.label {
  font-size: 1.2rem;
  color: #fff;
  opacity: 0.9;
}

.news-img-wrapper {
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: #eee;
}

.news-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.sup {
  vertical-align: super;
  font-size: 0.6em;
}

@media screen and (min-width: 1400px) {
  .about-title {
    font-size: 2.4rem;
    margin-top: 80px;
    margin-bottom: 120px;
  }

  .about-hero-content {
    width: 80%;
  }

  .about-desc {
    font-size: 1.2rem;
    line-height: 2;
  }

  .about-info h3 {
    font-size: 2rem;
  }

  .about-btn {
    font-size: 1.2rem;
    padding: 10px 50px;
  }

  .num {
    font-size: 2.5rem;
    font-weight: bold;
    margin-bottom: 20px;
  }

  .label {
    font-size: 1.1rem;
    color: #fff;
    opacity: 0.9;
  }

  .about-title::after {
    width: 80px;
  }

  .about-main {
    margin-top: 100px;
  }

}

@media screen and (max-width: 1399px) {
  .about-title {
    font-size: 2.2rem;
    margin-top: 70px;
    margin-bottom: 110px;
  }

  .about-hero-content {
    width: 82%;
  }

  .about-desc {
    font-size: 1rem;
    line-height: 2;
  }

  .about-info h3 {
    font-size: 1.8rem;
  }

  .about-btn {
    font-size: 1rem;
    padding: 10px 40px;
  }

  .num {
    font-size: 2.3rem;
    font-weight: bold;
    margin-bottom: 10px;
  }

  .label {
    font-size: 1rem;
    color: #fff;
    opacity: 0.9;
  }

  .about-title::after {
    width: 70px;
  }

  .about-main {
    margin-top: 90px;
  }
}

@media screen and (max-width: 991px) {
  .about-title {
    font-size: 2rem;
    margin-top: 60px;
    margin-bottom: 100px;
  }

  .about-hero-content {
    width: 84%;
  }

  .about-desc {
    font-size: 0.9rem;
    line-height: 2;
  }

  .about-info h3 {
    font-size: 1.6rem;
  }

  .about-btn {
    font-size: 0.9rem;
    padding: 10px 30px;
  }

  .num {
    font-size: 2.1rem;
    font-weight: bold;
    margin-bottom: 10px;
  }

  .label {
    font-size: 0.9rem;
    color: #fff;
    opacity: 0.9;
  }

  .about-title::after {
    width: 60px;
  }

  .about-main {
    margin-top: 80px;
  }
}

@media screen and (max-width: 767px) {
  .about-title {
    font-size: 1.8rem;
    margin-top: 50px;
    margin-bottom: 90px;
  }

  .about-hero-content {
    width: 86%;
  }

  .about-desc {
    font-size: 0.8rem;
    line-height: 2;
  }

  .about-info h3 {
    font-size: 1.4rem;
  }

  .about-btn {
    font-size: 0.9rem;
    padding: 10px 20px;
  }

  .num {
    font-size: 1.9rem;
    font-weight: bold;
    margin-bottom: 10px;
  }

  .label {
    font-size: 0.8rem;
    color: #fff;
    opacity: 0.9;
  }

  .about-title::after {
    width: 50px;
  }

  .about-main {
    margin-top: 70px;
  }
}

.suffix-small {
  font-size: 0.6em;
}
</style>
